import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, ArrowRightOnRectangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

/**
 * SessionTimeoutModal — Shows a countdown modal 5 minutes before JWT expiry.
 * Reads the token expiry from localStorage. Portal tokens should store exp claim.
 */
const WARNING_SECONDS = 5 * 60; // show modal 5 min before expiry

const getTokenExpiry = () => {
  // Try portal_token first, then regular token
  const token = localStorage.getItem('portal_token') || localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

const SessionTimeoutModal = ({ loginPath = '/login' }) => {const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const handleLogout = useCallback(() => {
    logout();
    navigate(loginPath);
  }, [logout, navigate, loginPath]);

  const handleStayLoggedIn = useCallback(() => {
    // Refresh page to trigger token refresh if the server supports it,
    // or simply dismiss for portal sessions (they re-auth on expiry).
    setShow(false);
    window.location.reload();
  }, []);

  useEffect(() => {
    let interval;

    const check = () => {
      const expiry = getTokenExpiry();
      if (!expiry) return;

      const msLeft = expiry - Date.now();
      const secsLeft = Math.floor(msLeft / 1000);

      if (msLeft <= 0) {
        clearInterval(interval);
        handleLogout();
        return;
      }

      if (secsLeft <= WARNING_SECONDS) {
        setSecondsLeft(secsLeft);
        setShow(true);
      } else {
        setShow(false);
      }
    };

    check(); // run immediately
    interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [handleLogout]);

  if (!show) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const isUrgent = secondsLeft <= 60;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-sm overflow-hidden animate-fade-in-up">
                <div className={`px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3 ${isUrgent ? 'bg-red-50 dark:bg-red-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isUrgent ? 'bg-red-100 dark:bg-red-900/40' : 'bg-amber-100 dark:bg-amber-900/40'}`}>
                        <ClockIcon className={`w-5 h-5 ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                    </div>
                    <div>
                        <h3 className={`text-base font-bold ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
                            {t("session_expiring_soon")}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("you_will_be_logged_out_automatically")}</p>
                    </div>
                </div>

                <div className="px-6 py-6 text-center">
                    <div className={`text-5xl font-black tabular-nums mb-2 ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {isUrgent ?
            'Your session is about to expire. Save your work!' :
            'Your session will expire in a few minutes. You can stay logged in or log out now.'}
                    </p>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                    <button
            onClick={handleLogout}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
            
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                        {t("log_out")}
                    </button>
                    <button
            onClick={handleStayLoggedIn}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all shadow-sm active:scale-95">
            
                        <ArrowPathIcon className="w-4 h-4" />
                        {t("stay_logged_in")}
                    </button>
                </div>
            </div>
        </div>);

};

export default SessionTimeoutModal;